import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getPost, updatePost } from "../lib/api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UpdatePostSchema } from "../lib/api";
import { z } from "zod";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

export default function EditPost() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: post, isLoading } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => {
      if (!postId) throw new Error("Post ID is required");
      return getPost(postId);
    },
    enabled: !!postId,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(UpdatePostSchema),
    defaultValues: post
      ? {
          title: post.title,
          body: post.body,
          tags: post.tags,
        }
      : undefined,
  });

  const mutation = useMutation({
    mutationFn: (data: z.infer<typeof UpdatePostSchema>) => {
      if (!postId) throw new Error("Post ID is required");
      return updatePost(postId, data);
    },
    onSuccess: (post) => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      navigate(`/app/posts/${post._id}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-semibold">Loading post...</h2>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600">
            Error loading post
          </h2>
          <p className="mt-2 text-gray-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  const components: Components = {
    h1: ({ ...props }) => <h1 className="text-3xl font-bold my-4" {...props} />,
    h2: ({ ...props }) => <h2 className="text-2xl font-bold my-3" {...props} />,
    h3: ({ ...props }) => <h3 className="text-xl font-bold my-2" {...props} />,
    p: ({ ...props }) => <p className="my-2" {...props} />,
    em: ({ ...props }) => <em className="italic" {...props} />,
    strong: ({ ...props }) => <strong className="font-bold" {...props} />,
    blockquote: ({ ...props }) => (
      <blockquote className="border-l-4 border-gray-200 pl-4 my-4" {...props} />
    ),
    code: ({ node, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "");
      const isInline = node?.position?.start.line === node?.position?.end.line;

      if (!isInline) {
        return (
          <pre className="bg-gray-100 p-4 rounded overflow-x-auto">
            <code className={match ? `language-${match[1]}` : ""} {...props}>
              {children}
            </code>
          </pre>
        );
      }

      return (
        <code className="bg-gray-100 rounded px-1" {...props}>
          {children}
        </code>
      );
    },
  };

  const currentBody = watch("body") || "";

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Edit Post</h1>
        <p className="mt-2 text-gray-600">Make changes to your post</p>
      </div>

      <form
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        className="space-y-6"
      >
        <div>
          <label htmlFor="title" className="label">
            Title
          </label>
          <input
            type="text"
            id="title"
            {...register("title")}
            className="input mt-1"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">
              {errors.title.message as string}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="body" className="label">
              Content (Markdown supported)
            </label>
            <textarea
              id="body"
              rows={10}
              {...register("body")}
              className="input mt-1 font-mono"
            />
            {errors.body && (
              <p className="mt-1 text-sm text-red-600">
                {errors.body.message as string}
              </p>
            )}
          </div>

          <div>
            <label className="label">Preview</label>
            <article className="prose prose-lg max-w-none rounded-md bg-white p-6">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
              >
                {currentBody}
              </ReactMarkdown>
            </article>
          </div>
        </div>

        <div>
          <label htmlFor="tags" className="label">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            id="tags"
            defaultValue={post.tags.join(", ")}
            {...register("tags", {
              setValueAs: (value: string | undefined) =>
                typeof value === "string"
                  ? value.split(",").map((tag) => tag.trim())
                  : [],
            })}
            className="input mt-1"
          />
          {errors.tags && (
            <p className="mt-1 text-sm text-red-600">
              {errors.tags.message as string}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() =>
              navigate(`/app/posts/${postId}`)
            }
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="btn btn-primary"
          >
            Update Post
          </button>
        </div>
      </form>
    </div>
  );
}
